import * as React from "react";
import { useMemo } from "react";
import { useHistory, useParams } from "react-router";
import { Button, Grid, Loader } from "semantic-ui-react";

import {
  CoachSettings,
  PricePlanTier,
  VideoProjectDetail,
  VideoProjectMessage,
  isEditingInProgress,
} from "@/interfaces";
import { fromVideoProject, SharedPostFlowDefaults } from "@/components/PostDialogs/SharedPostFlowDefaults";
import { PageActionProps, PageTitle } from "@/components/PageTitle";
import { PlayerPostDialog } from "@/components/PostDialogs/PlayerPostDialog";
import { SideBar } from "@/components/SideBar";
import { SocialNetworksSection } from "@/components/SocialNetworksSection";
import { TitleSection } from "@/components/TitleSection";
import { VideoProjectDetailComponent } from "@/components/Forms/VideoProjectDetail";
import { VideoProjectServices } from "@/services";

import { CustomBox, CustomColor, RowBasicPadding, SocialGrid, WhiteContainer } from "@/styling/baseStyle";
import "./styles.scss";
import { VideoProjectBubbleDetails } from "../../Editor/VideoProjectPage/components/BubbleDetails";
import {
  URLS,
  urlWithFormat,
  usePlayerSettings,
  useSocialCoachSession,
  useSocialNetworks,
  validatePendingAck,
} from "@/utils";
import moment from "moment";
import { buildPreferenceOptions, StylePreferencesOptions } from "@/utils/projectHelpers";
import { VideoPageModal } from "@/containers/VideoPages/VideoPageModal";

type Props = {
  embedded?: boolean;
};

const VideoProjectDetailsPageFC: React.FC<Props> = ({ embedded = false }) => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { isPlayer, playerId, session } = useSocialCoachSession();
  const videoProjectId = isFinite(Number(id)) ? Number(id) : undefined;

  // Properties
  const [loading, setLoading] = React.useState<boolean>(false);
  const [videoProject, setVideoProject] = React.useState<VideoProjectDetail | undefined>(undefined);
  const [videoProjectMessages, setVideoProjectMessages] = React.useState<VideoProjectMessage[] | undefined>(undefined);
  const [needsAck, setNeedsAck] = React.useState<boolean>(false);
  const [postSnUrls, setPostSnUrls] = React.useState<{ [key: string]: string }>({});
  const [showPostModal, setShowPostModal] = React.useState<boolean>(false);
  const [showVideoPageModal, setShowVideoPageModal] = React.useState<boolean>(false);
  const [preferencesInfo, setPreferencesInfo] = React.useState<StylePreferencesOptions[]>([]);
  const { hasSetting } = usePlayerSettings(playerId);

  const { postableSocialNetworks } = useSocialNetworks();

  const isSCPlayer = isPlayer && session?.subscription?.tier == PricePlanTier.PLAYER_PLUS;

  const editedMedia = React.useMemo(() => {
    return videoProject?.editedMedia?.media?.[0] ?? undefined;
  }, [videoProject?.editedMedia?.media?.[0]]);

  const readyToPost = React.useMemo(() => {
    return isSCPlayer && editedMedia !== undefined;
  }, [isSCPlayer, editedMedia]);

  const canShareVideo = React.useMemo(() => {
    return readyToPost && hasSetting(CoachSettings.PLAYER_MVP_ACCESS);
  }, [readyToPost, hasSetting]);

  const goBack = () => {
    if (isSCPlayer) {
      history.replace(URLS.player.videoProjects);
    } else {
      history.goBack();
    }
  };

  React.useEffect(() => {
    setPreferencesInfo(buildPreferenceOptions(videoProject?.preferences));
  }, [videoProject?.preferences]);

  React.useEffect(() => {
    let snUrls = {};

    videoProject?.post?.socialNetworks?.map(sn => {
      if (sn.externalPostUrl && sn.socialNetworkId !== "FB") {
        snUrls = {
          ...snUrls,
          [sn.socialNetworkId]: urlWithFormat(sn.externalPostUrl!.replace("[", "").replace("]", "")),
        };
      } else {
        snUrls = {
          ...snUrls,
          [sn.socialNetworkId]: sn.externalPostUrl!,
        };
      }
    });
    setPostSnUrls(snUrls);
  }, [videoProject?.post?.socialNetworks]);

  React.useEffect(() => {
    const ROLE = isSCPlayer ? "PLAYER" : "COACH";
    if (videoProjectId) {
      setLoading(true);
      VideoProjectServices.get(videoProjectId)
        .then(vp => {
          setVideoProject(vp);
          setVideoProjectMessages(vp.messages);
          setNeedsAck(validatePendingAck(vp.ackList, ROLE));
        })
        .catch(() => {
          alert("Error getting video project info");
          goBack();
        })
        .finally(() => {
          setLoading(false);
        });
      VideoProjectServices.acknowledgeMessage(videoProjectId).catch(e => {
        console.error("acknowledgeMessage error", e);
      });
    }
  }, [videoProjectId]);

  const refreshMessages = (): void => {
    VideoProjectServices.get(`${videoProject?.videoProjectId!!}`).then((r: VideoProjectDetail) => {
      setVideoProjectMessages(r.messages);
    });
  };

  const postSection = (): JSX.Element => {
    const postSnIds = videoProject?.post?.socialNetworks
      ?.filter(sn => sn.status === "POSTED")
      .map(sn => sn.socialNetworkId);
    return (
      <div className="flex flex-col gap-2 -ml-3.5">
        <label className="mb-2">Project Info</label>
        {videoProject && (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1 text-sm">
              <div className="font-semibold">Status:</div>
              <div>
                {`${videoProject?.statusLabel} ${moment(videoProject?.statusDate).format("MM/DD/YYYY h:mm a")}`}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <div className="font-semibold">Created:</div>
              <div>{moment(videoProject?.createdDate).format("MM/DD/YYYY h:mm a")}</div>
            </div>
          </div>
        )}
        {postSnIds && postSnIds.length > 0 && (
          <SocialNetworksSection actives={postSnIds} links={postSnUrls} allSocialNetworks={postableSocialNetworks} />
        )}
      </div>
    );
  };

  const preferencesSection = () => {
    return (
      <SocialGrid.Row className={"noPadding"}>
        <SocialGrid.Column width={10}>
          <SocialGrid className={"videoProjectContainer"}>
            <RowBasicPadding>
              <label className={"sectionHeader"}>Style Preferences</label>
            </RowBasicPadding>

            <RowBasicPadding className="stylePreferencesRow">
              {preferencesInfo.map(preference => {
                return (
                  <p key={preference.key} className="noPadding">
                    {`\u2022 ${preference.key}`}
                    {preference.colors?.map(color => {
                      return (
                        <CustomBox>
                          <CustomColor fill={color} />
                          {color.toUpperCase()}
                        </CustomBox>
                      );
                    })}
                  </p>
                );
              })}
            </RowBasicPadding>
          </SocialGrid>
        </SocialGrid.Column>
      </SocialGrid.Row>
    );
  };

  const postDefaults: SharedPostFlowDefaults | undefined = React.useMemo(() => {
    return videoProject ? fromVideoProject(videoProject) : undefined;
  }, [videoProject]);

  const videoPageModalInitialObject = useMemo(() => {
    if (editedMedia) return { media: { ...editedMedia, id: -1 } };
    return undefined;
  }, [editedMedia]);

  const content = () => {
    return (
      <Grid.Row columns={1} className={"videoProjectDetailContainer leftPadding"}>
        <Grid className={"infoSection"}>
          <Grid.Column width={8} className={"formContainer"}>
            {videoProject && <VideoProjectDetailComponent videoProject={videoProject!!} />}
          </Grid.Column>
          <Grid.Column width={6}>
            {videoProject && postSection()}
            {videoProject && preferencesSection()}
            {videoProjectMessages && (
              <VideoProjectBubbleDetails
                onSuccess={returnedProject => {
                  if (returnedProject && returnedProject.videoProjectId !== videoProjectId) {
                    const url = (isPlayer ? URLS.player.videoProject : URLS.coach.videoProject).replace(
                      ":id",
                      `${returnedProject.videoProjectId}`
                    );
                    history.replace(url);
                  } else {
                    refreshMessages();
                  }
                }}
                videoProjectId={videoProject?.videoProjectId!!}
                placeholder="Add a message to request a change..."
                submitBtnLabel={isEditingInProgress(videoProject?.status) ? "Add Message" : "Request Changes"}
                noteMessage={
                  !videoProject?.editable
                    ? "Note: Because this project has already been posted, this request will create a new project"
                    : undefined
                }
                messages={videoProjectMessages}
                needsAck={needsAck}
              />
            )}
          </Grid.Column>
        </Grid>

        {canShareVideo && (
          <VideoPageModal
            open={showVideoPageModal}
            initialObject={videoPageModalInitialObject}
            onClose={() => setShowVideoPageModal(false)}
          />
        )}

        {readyToPost && showPostModal && postDefaults && (
          <PlayerPostDialog
            open={showPostModal}
            postId={videoProject?.post?.postId}
            playerId={videoProject?.playerId}
            defaults={postDefaults}
            handleClose={() => {
              setShowPostModal(false);
            }}
          />
        )}
      </Grid.Row>
    );
  };

  if (embedded) {
    const actions: PageActionProps[] = [];
    if (readyToPost) {
      actions.push({
        text: "Post",
        onClick: () => setShowPostModal(true),
      });
    }
    if (canShareVideo) {
      actions.push({
        text: "Create Video Page",
        onClick: () => setShowVideoPageModal(true),
      });
    }
    return (
      <div>
        <PageTitle
          title="Video Catalyst Project"
          actions={actions}
          breadcrumbs={[{ name: "Library", link: URLS.coach.videoProjects }]}
        />
        {content()}
      </div>
    );
  } else {
    const extraOptions = () => {
      return (
        <Grid.Column width={8}>
          <Grid columns={1}>
            <Grid.Column>
              <Button
                type="submit"
                loading={loading}
                fluid
                className={"secondary rounded buttonRight"}
                onClick={() => {
                  setShowVideoPageModal(true);
                }}
              >
                Create Video Page
              </Button>
            </Grid.Column>
          </Grid>
        </Grid.Column>
      );
    };

    return (
      <WhiteContainer>
        <SideBar history={history} />
        {loading ? (
          <Loader active size="large" />
        ) : (
          <Grid columns={1} className="contentSection">
            <TitleSection
              title="Video Catalyst Project"
              backLabel="Back to Video Projects"
              handleBackAction={goBack}
              disableButtons={false}
              titleSize={8}
              buttonsSize={8}
              showLinkOption={canShareVideo}
              showNextOption={readyToPost}
              customNextLabel="Post"
              handleNextAction={() => setShowPostModal(true)}
              customLinkSection={extraOptions()}
            />
            {content()}
          </Grid>
        )}
      </WhiteContainer>
    );
  }
};

export const VideoProjectDetailsPage = VideoProjectDetailsPageFC;
